/**
 * Routing, and the gate in front of each shell.
 *
 * Note what the child routes do *not* contain: an identifier. `/eleve` and its
 * children name nobody. The parent routes carry a `childId` because a parent
 * legitimately names a child — and every one of those reads is journalled
 * server-side before the handler runs (`core-api` §9).
 */

import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";

import { SessionProvider, useSession } from "@/app/session";
import { ChildSignIn, ParentSignIn } from "@/app/SignInScreens";
import { AbsenceScreen } from "@/child/AbsenceScreen";
import { ChildShell } from "@/child/ChildShell";
import { JournalRattrapageScreen } from "@/child/JournalRattrapageScreen";
import { JournalScreen } from "@/child/JournalScreen";
import { JournalHistoriqueScreen } from "@/child/JournalHistoriqueScreen";
import { RevisionScreen } from "@/child/RevisionScreen";
import { SerieFlashScreen } from "@/child/SerieFlashScreen";
import { PositionnementScreen } from "@/child/PositionnementScreen";
import { CarteScreen } from "@/child/CarteScreen";
import { MesCahiersScreen, MonCahierScreen, MonChapitreScreen } from "@/child/MesCahiers";
import { MesEntrainementsTermines } from "@/child/MesEntrainementsTermines";
import { MonEntrainementLayout } from "@/child/MonEntrainementLayout";
import { MonEntrainementScreen } from "@/child/MonEntrainementScreen";
import { MonGenererScreen } from "@/child/MonGenererScreen";
import { ProgressScreen } from "@/child/ProgressScreen";
import { SessionScreen } from "@/child/SessionScreen";
import { TravailScreen } from "@/child/TravailScreen";
import { OnboardingScreen, hasSignedOnboarding } from "@/onboarding/OnboardingScreen";
import { AdminShell } from "@/admin/AdminShell";
import { AuditScreen } from "@/admin/AuditScreen";
import { DataScreen } from "@/admin/DataScreen";
import { DevicesScreen } from "@/admin/DevicesScreen";
import { NotificationsScreen } from "@/admin/NotificationsScreen";
import { SessionsScreen } from "@/admin/SessionsScreen";
import { AgentChatScreen } from "@/parent/AgentChatScreen";
import { CahierScreen } from "@/parent/CahierScreen";
import { VuEnClasseLayout } from "@/parent/VuEnClasseLayout";
import { VuEnClasseScreen } from "@/parent/VuEnClasseScreen";
import { ClasseScreen } from "@/parent/ClasseScreen";
import { CahierDeLaSemaineScreen } from "@/parent/CahierDeLaSemaineScreen";
import { ChildLayout } from "@/parent/ChildLayout";
import { ChildDetailScreen } from "@/parent/ChildDetailScreen";
import { DashboardScreen } from "@/parent/DashboardScreen";
import { EffortScreen } from "@/parent/EffortScreen";
import { ProchainScreen } from "@/parent/ProchainScreen";
import { JournalScreen as JournalParents } from "@/parent/JournalScreen";
import { ParentShell } from "@/parent/ParentShell";
import { SummariesScreen } from "@/parent/SummariesScreen";
import { TravailDemandeScreen } from "@/parent/TravailDemandeScreen";
import { ValidationScreen } from "@/parent/ValidationScreen";
import { VueDEnsembleLayout } from "@/parent/VueDEnsembleLayout";
import { AncienneAdresseDeLEnfant } from "@/v2/AncienneAdresse";
import { CarnetScreen } from "@/v2/CarnetScreen";
import { CatalogueScreen } from "@/v2/CatalogueScreen";
import { ChapitreScreen } from "@/v2/ChapitreScreen";
import { ChapitresScreen } from "@/v2/ChapitresScreen";
import { EntrainementsScreen } from "@/v2/EntrainementsScreen";
import { GenererScreen } from "@/v2/GenererScreen";
import { SujetsScreen } from "@/v2/SujetsScreen";
import { V2ChildLayout } from "@/v2/V2ChildLayout";
import { SeanceADeuxScreen } from "@/v2/SeanceADeuxScreen";
import { ValiderScreen } from "@/v2/ValiderScreen";
import { BarreDeChargement } from "@/ui/BarreDeChargement";
import { Loading } from "@/ui/primitives";

export function App() {
  return (
    <SessionProvider>
      <BrowserRouter>
        {/* **Une seule barre d'attente, au-dessus des quatre coquilles.** Elle
            est ici et non dans chacune parce que la demande portait sur tout le
            site, et parce qu'une coquille écrite demain doit en hériter sans y
            penser (25 septembre 2026). */}
        <BarreDeChargement />
        <Shells />
      </BrowserRouter>
    </SessionProvider>
  );
}

/**
 * L'adresse demandée avant la connexion.
 *
 * Arriver sur `/v2` sans session affiche l'écran de connexion, et « Espace
 * parents » est un lien qui **recharge la page** : l'adresse devenait
 * `/connexion`, que le routeur ne reconnaît pas une fois la session ouverte,
 * donc la route de repli renvoyait sur `/parents`. Le lien profond était perdu
 * en silence, et une V2 correcte se lisait comme une redirection.
 *
 * `sessionStorage` et non un état React, précisément parce que la page se
 * recharge : rien en mémoire ne survit à ce lien. Elle est **consommée une
 * fois** — `reprendre` efface en lisant — sans quoi toute navigation
 * ultérieure y ramènerait.
 */
const SUITE = "tuteur.suite";

function retenirLaSuite(): void {
  const suite = `${window.location.pathname}${window.location.search}`;
  // La racine et l'écran de connexion ne sont pas des destinations : les
  // retenir ferait revenir sur l'écran dont on vient de sortir.
  if (suite === "/" || window.location.pathname === "/connexion") return;
  try {
    window.sessionStorage.setItem(SUITE, suite);
  } catch {
    /* un stockage refusé ne doit pas empêcher de se connecter */
  }
}

function reprendreLaSuite(): string | null {
  try {
    const suite = window.sessionStorage.getItem(SUITE);
    if (suite) window.sessionStorage.removeItem(SUITE);
    return suite;
  } catch {
    return null;
  }
}

function Shells() {
  const { status, me, isChild, isParent } = useSession();
  const [onboarded, setOnboarded] = useState(() => hasSignedOnboarding(me?.person_id));
  const naviguer = useNavigate();

  useEffect(() => {
    if (status === "anonymous") {
      retenirLaSuite();
      return;
    }
    if (status !== "signed-in") return;
    const suite = reprendreLaSuite();
    // La route de repli monte au même rendu et navigue dans son propre effet ;
    // React exécute les effets des enfants d'abord, donc celui-ci passe après
    // et c'est l'adresse demandée qui reste.
    if (suite) naviguer(suite, { replace: true });
  }, [status, naviguer]);

  if (status === "loading") return <Loading />;

  if (status === "anonymous") {
    return (
      <Routes>
        <Route path="/connexion" element={<ParentSignIn />} />
        <Route path="*" element={<ChildSignIn />} />
      </Routes>
    );
  }

  if (isChild && me && !onboarded && !hasSignedOnboarding(me.person_id)) {
    return (
      <OnboardingScreen
        personId={me.person_id}
        displayName={me.display_name}
        onDone={() => setOnboarded(true)}
      />
    );
  }

  return (
    <Routes>
      {isChild ? (
        <Route path="/eleve" element={<ChildShell />}>
          {/* **Le journal est l'index**, depuis le 3 septembre 2026 : le
              premier écran est celui où l'enfant raconte sa journée, et le
              travail vient après (web-pwa §5.6). Il n'est plus bloquant depuis
              le 30 septembre 2026 : toutes les routes ci-dessous s'ouvrent,
              journal envoyé ou non. */}
          <Route index element={<JournalScreen />} />
          <Route path="journal" element={<JournalHistoriqueScreen />} />
          {/* Rattraper un jour oublié (8 septembre 2026). */}
          <Route path="journal/rattrapage/:le" element={<JournalRattrapageScreen />} />
          <Route path="travail" element={<TravailScreen />} />
          <Route path="seance" element={<SessionScreen />} />
          {/* L'ancien mot du soir. La PWA installée sur les deux tablettes garde
              cette URL dans son historique ; elle mène désormais au journal, qui
              pose les mêmes questions et les enregistre, lui. */}
          <Route path="compte-rendu" element={<Navigate to="/eleve" replace />} />
          <Route path="revision" element={<RevisionScreen />} />
          {/* Une série d'automatismes, chronométrée et entière (`exercise`
              §10). Elle sert des exercices, donc elle passe derrière le journal
              comme le travail et la séance. La compétence est dans l'URL et
              jamais l'enfant (invariant 2) ; on y arrive depuis « Mon travail »
              ou depuis la révision, qui marquent la compétence `serie`. */}
          <Route path="flash/:competencyId" element={<SerieFlashScreen />} />
          {/* « Se situer » — le test de positionnement. Il sert des exercices,
              donc il passe derrière le journal comme le travail et la séance. */}
          <Route path="positionnement" element={<PositionnementScreen />} />
          <Route path="progression" element={<ProgressScreen />} />
          {/* « Ma carte » — tout le programme, vu / non vu, travaillé / non
              travaillé. La route existe pour les deux enfants ; seul le profil
              lycée en propose l'onglet (`config/profile.ts`). */}
          <Route path="carte" element={<CarteScreen />} />
          {/* « Entraînement » — l'élève compose ses propres feuilles, les
              travaille sur papier et les clôt pour lire la correction (Hedi,
              5 octobre 2026, web-pwa §5.11). Seul le profil lycée en propose
              l'onglet ; aucun identifiant d'enfant dans l'adresse. */}
          <Route path="entrainement" element={<MonEntrainementLayout />}>
            <Route index element={<MonEntrainementScreen />} />
            <Route path="generer" element={<MonGenererScreen />} />
            <Route path="cahiers" element={<MesCahiersScreen />} />
            <Route path="cahiers/:ouvrage" element={<MonCahierScreen />} />
            <Route path="cahiers/:ouvrage/:cle" element={<MonChapitreScreen />} />
            <Route path="termines" element={<MesEntrainementsTermines />} />
          </Route>
          <Route path="absence" element={<AbsenceScreen />} />
        </Route>
      ) : null}

      {isParent ? (
        <>
          <Route path="/parents" element={<ParentShell />}>
            <Route index element={<DashboardScreen />} />
            {/* **Un layout, et non six routes sœurs** (6 septembre 2026). Elles
                l'étaient, et c'est pour cela que les liens d'un enfant
                disparaissaient dès qu'on en suivait un : ouvrir « Le travail
                fourni » remplaçait la page entière, y compris ce qui menait aux
                cinq autres écrans. La rangée de sous-onglets vit maintenant dans
                `ChildLayout`, au-dessus de l'`<Outlet/>`. */}
            <Route path="enfant/:childId" element={<ChildLayout />}>
              {/* « Vue d'ensemble » et ses quatre sous-onglets (27 septembre
                  2026). La route est **sans chemin** : elle regroupe sans
                  déplacer, si bien que `cahier`, `effort` et `resumes` gardent
                  les adresses que le tableau de bord et la fiche citent déjà. */}
              <Route element={<VueDEnsembleLayout />}>
                <Route index element={<ChildDetailScreen />} />
                <Route path="cahier" element={<CahierScreen />} />
                <Route path="effort" element={<EffortScreen />} />
                <Route path="resumes" element={<SummariesScreen />} />
              </Route>
              {/* « Entraînements » : les cahiers achetés, montés depuis `v2/`
                  pour l'enfant choisi sur la barre du haut (27 septembre 2026 ;
                  ils vivaient sous `/v2` avec leur propre choix d'enfant). Un
                  **layout** et non des routes sœurs : la rangée doit survivre à
                  l'ouverture d'un cahier et d'un chapitre (26 septembre 2026).
                  `src/v2/` n'importe toujours rien de `parent/` ; c'est ce
                  fichier qui monte les deux. */}
              <Route path="entrainements" element={<V2ChildLayout />}>
                <Route index element={<CatalogueScreen />} />
                {/* **Des segments fixes devant `:ouvrage`**, et non des routes
                    sœurs : React Router classe le littéral avant le paramètre,
                    donc ni `composes` ni `a-valider` ne peuvent être pris pour
                    une clé de cahier. Ce serait faux si un ouvrage s'appelait
                    ainsi un jour ; les clés sont celles des éditeurs
                    (`bescherelle-maxi`), et le catalogue les liste. */}
                <Route path="composes" element={<EntrainementsScreen />} />
                <Route path="sujets" element={<SujetsScreen />} />
                <Route path="carnet" element={<CarnetScreen />} />
                <Route path="a-valider" element={<ValiderScreen />} />
                {/* La séance à deux (lot P4) : `seance` est un littéral, donc
                    classé avant `:ouvrage/:cle`. */}
                <Route path="seance/:seanceId" element={<SeanceADeuxScreen />} />
                <Route path="generer" element={<GenererScreen />} />
                <Route path=":ouvrage" element={<ChapitresScreen />} />
                <Route path=":ouvrage/:cle" element={<ChapitreScreen />} />
              </Route>
              {/* Le plan du soir avant le soir, et les mots des professeurs
                  derrière les compétences qu'on en tire (6 septembre 2026). */}
              <Route path="prochain" element={<ProchainScreen />} />
              {/* L'échéancier : le travail demandé et les évaluations
                  annoncées, par jour (15 septembre 2026). Les deux sources
                  existaient et aucun écran ne les montrait ensemble. */}
              <Route path="travail-demande" element={<TravailDemandeScreen />} />
              <Route path="classe" element={<ClasseScreen />} />
              {/* Ce que le parent déclare traité en classe, et le tuteur qui
                  aide à trancher (8 septembre 2026). La seule porte par
                  laquelle une preuve manquante peut être apportée. */}
              <Route path="vu-en-classe" element={<VuEnClasseLayout />}>
                <Route index element={<VuEnClasseScreen />} />
                {/* Les mêmes compétences, rangées sous les chapitres des
                    ouvrages ou, à défaut, sous les thèmes du programme
                    (27 septembre 2026). */}
                <Route path="par-chapitre" element={<VuEnClasseScreen vue="chapitres" />} />
                {/* Le cahier de classe photographié en fin de semaine (lot P5). */}
                <Route path="cahier-semaine" element={<CahierDeLaSemaineScreen />} />
              </Route>
              <Route path="journal" element={<JournalParents />} />
              <Route path="echanger" element={<AgentChatScreen />} />
            </Route>
            <Route path="validation" element={<ValidationScreen />} />
            {/* Le lien profond de Telegram. Il nomme l'exercice, que l'écran
                remonte en tête au lieu de l'isoler : un parent qui vient pour
                un travail et en a deux en attente doit voir le second. */}
            <Route path="validation/:exerciseId" element={<ValidationScreen />} />
          </Route>
          {/* Les anciennes adresses de la V2 (24–26 septembre 2026), gardées
              en redirection : l'historique de la PWA installée et l'adresse
              retenue au travers de la connexion peuvent encore les porter.
              `/v2` seul ne nomme aucun enfant et mène au tableau de bord. */}
          <Route path="/v2" element={<Navigate to="/parents" replace />} />
          <Route path="/v2/enfant/:childId/*" element={<AncienneAdresseDeLEnfant />} />
          <Route path="/reglages" element={<AdminShell />}>
            <Route index element={<DevicesScreen />} />
            <Route path="sessions" element={<SessionsScreen />} />
            <Route path="notifications" element={<NotificationsScreen />} />
            <Route path="journal" element={<AuditScreen />} />
            <Route path="donnees" element={<DataScreen />} />
          </Route>
        </>
      ) : null}

      <Route path="*" element={<Navigate to={isChild ? "/eleve" : "/parents"} replace />} />
    </Routes>
  );
}
